import { Box, Chip, Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import PauseCircleOutline from '@mui/icons-material/PauseCircleOutline'
import PersonOutline from '@mui/icons-material/PersonOutline'
import {
  formatAccessLogTimestamp,
  PRACTITIONER_TYPE_LABEL,
} from 'src/supportPortal/utils/practitionerStatus'

const EntryCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 1.75),
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  background: 'rgba(255, 255, 255, 0.9)',
}))

const Marker = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'enabled',
})(({ enabled }) => ({
  flexShrink: 0,
  width: 34,
  height: 34,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  background: enabled
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
}))

/**
 * Presentational, newest-first list of practitioner enable/disable access-log entries.
 * Shared by the per-practitioner detail section and the global access-logs page —
 * `showPractitioner` turns on the practitioner name + type line for the global view.
 */
export default function PractitionerAccessLogList({ logs = [], showPractitioner = false }) {
  if (!Array.isArray(logs) || logs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
        <Typography variant="body2">No access-log entries yet.</Typography>
      </Box>
    )
  }

  return (
    <Stack spacing={1.25}>
      {logs.map((log, index) => {
        const enabled = log?.action === 'ENABLED' || log?.enabledAfter === true
        const typeLabel =
          PRACTITIONER_TYPE_LABEL[log?.practitionerType] || log?.practitionerType
        return (
          <EntryCard key={log?.id || `${log?.createdTime}-${index}`}>
            <Marker enabled={enabled}>
              {enabled ? (
                <CheckCircleOutline sx={{ fontSize: 20 }} />
              ) : (
                <PauseCircleOutline sx={{ fontSize: 20 }} />
              )}
            </Marker>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {enabled ? 'Enabled' : 'Disabled'}
                </Typography>
                {showPractitioner && log?.practitionerName && (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {log.practitionerName}
                  </Typography>
                )}
                {showPractitioner && typeLabel && (
                  <Chip size="small" variant="outlined" label={typeLabel} />
                )}
                <Typography variant="caption" color="text.secondary">
                  {formatAccessLogTimestamp(log?.createdTime)}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                <PersonOutline sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  {log?.actorName || log?.actorUserId || 'Unknown actor'}
                </Typography>
              </Stack>
              {log?.reason && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.75,
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    background: 'rgba(0, 0, 0, 0.03)',
                    color: 'text.primary',
                  }}
                >
                  “{log.reason}”
                </Typography>
              )}
            </Box>
          </EntryCard>
        )
      })}
    </Stack>
  )
}
